<template>
  <el-dialog
    :close-on-click-modal="false"
    title="提现申请"
    :visible.sync="innerVisible"
    top="5vh"
    :width="layout.is_phone ? '90%' : '80%'"
    :append-to-body="true"
    custom-class="custom-dialog"
  >
    <el-form
      ref="form"
      :model="form"
      :rules="rules"
      :label-width="layout.is_phone ? '80px' : '100px'"
      style="max-height: 65vh; overflow: auto;"
    >
      <!-- 提示区 -->
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="mb12"
        :title="`提现说明（手续费 ${ (feeRate*100).toFixed(2) }%）`"
        :description="`单笔最低 ${minAmount}；最高为可用余额 ${balance.toFixed(2)}。提交后预计 T+1 人工审核打款。`"
      />

      <el-row >
        <el-col :xs="24" :sm="12">
          <!-- 提现金额 -->
          <el-form-item label="提现金额" prop="amount">
            <el-input-number
              v-model="form.amount"
              :min="minAmount"
              :max="balance"
              :precision="2"
              :step="1"
              controls-position="right"
              @change="onAmountChange"
              class="w200"
            />
            <el-tooltip effect="dark" placement="top">
              <div slot="content">
                可用余额：{{ balance.toFixed(2) }}<br />
                将自动按范围 {{ minAmount }} ~ {{ balance.toFixed(2) }} 
              </div>
              <i class="el-icon-info ml8"></i>
            </el-tooltip>
            <el-button type="text" class="ml8" @click="fillAll">全部提现</el-button>
          </el-form-item>

          <!-- 网络选择 -->
          <el-form-item label="网络" prop="chain">
            <el-select v-model="form.chain" placeholder="请选择网络" class="w200">
              <el-option label="TRC20（USDT）" value="TRC20" />
              <!-- <el-option label="ERC20（USDT）" value="ERC20" /> -->
            </el-select>
          </el-form-item>

          <!-- 钱包地址 -->
          <el-form-item label="钱包地址" prop="address">
            <el-input
              v-model.trim="form.address"
              placeholder="请输入收款钱包地址"
              clearable
              class="w100p"
            >
              <template slot="append">
               <el-button type="text" @click="pasteFromClipboard">粘贴</el-button>
                <!-- <el-divider direction="vertical"></el-divider> -->
                <!-- <el-button type="text" @click="quickCheckAddress">校验</el-button> -->
              </template>
            </el-input>
            <div class="addr-hint">
              为避免丢币，请确保地址与所选网络一致
			  <!-- （如 TRC20 常以 T 开头，长度约 34；ERC20 为 0x 开头 42 位）。 -->
            </div>
          </el-form-item>

          <!-- 备注（可选） -->
          <el-form-item label="备注">
            <el-input
              v-model.trim="form.remark"
              placeholder="可填写用途/备注，选填"
              maxlength="60"
              show-word-limit
            />
          </el-form-item>
        </el-col>
		<el-col :xs="24" :sm="1">
			<div style="color: #fff;">.</div>
		</el-col>
        <!-- 右侧结算卡片 -->
        <el-col :xs="24" :sm="11">
          <el-card shadow="hover" class="summary-card">
            <div class="sum-row">
              <span>提现金额</span>
              <b>{{ displayAmount }}</b>
            </div>
            <div class="sum-row">
              <span>手续费</span>
              <b>− {{ feeAmount.toFixed(2) }}</b>
            </div>
            <el-divider />
            <div class="sum-row total">
              <span>实际到账</span>
              <b>{{ netAmount.toFixed(2) }}</b>
            </div>
            <div class="sum-foot">可用余额：{{ balance.toFixed(2) }}</div>
          </el-card>

          <el-alert
            type="warning"
            :closable="false"
            show-icon
            class="mt12"
            title="风险提示"
            description="提交后将锁定对应余额，请务必核对网络与地址；地址错误造成的损失难以追回。"
          />
        </el-col>
      </el-row>
    </el-form>

    <div slot="footer" class="dialog-footer">
      <el-button @click="onCancel">取消</el-button>
      <el-button
        type="primary"
        :loading="layout.loading"
        :disabled="layout.loading || disableConfirm"
        @click="submit"
      >
        确认
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
export default {
  name: 'WithdrawApplyDialog',
  props: {
    // v-model 控制对话框显示
    value: { type: Boolean, default: false },
    // 可用余额（最高上限）
    balance: { type: Number, default: 0 },
    // 手续费比例（例如 0.012 表示 1.2%）
    feeRate: { type: Number, default: 0.01 },
    // 最低提现金额
    minAmount: { type: Number, default: 10 },
    // 响应式宽度控制
    isPhone: { type: Boolean, default: false }
  },
  data() {
    return {
      innerVisible: this.value,
      layout: {
        loading: false,
        is_phone: this.isPhone
      },
      form: {
        amount: null,
        chain: 'TRC20',
        address: '',
        remark: ''
      },
      rules: {
        amount: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          {
            validator: (rule, value, cb) => {
              const v = Number(value)
              if (!Number.isFinite(v)) return cb(new Error('金额格式不正确'))
              if (v < this.minAmount) return cb(new Error(`金额不能低于 ${this.minAmount}`))
              if (v > this.balance) return cb(new Error(`金额不能高于可用余额 ${this.balance.toFixed(2)}`))
              cb()
            },
            trigger: ['blur', 'change']
          }
        ],
        chain: [{ required: true, message: '请选择网络', trigger: 'change' }],
        address: [
          { required: true, message: '请输入钱包地址', trigger: 'blur' },
          {
            validator: (rule, value, cb) => {
              const ok = this.checkAddressByChain(value, this.form.chain)
              ok ? cb() : cb(new Error('地址与所选网络不匹配或格式错误'))
            },
            trigger: 'blur'
          }
        ]
      }
    }
  },
  computed: {
    displayAmount() {
      return this.form.amount ? Number(this.form.amount).toFixed(2) : '0.00'
    },
    // 手续费 = 提现金额 × 费率（两位小数）；防止极端为负或 NaN
    feeAmount() {
      const a = Number(this.form.amount) || 0
      const f = Number((a * this.feeRate).toFixed(2))
      return f > 0 ? f : 0
    },
    netAmount() {
      const a = Number(this.form.amount) || 0
      const n = a - this.feeAmount
      return n > 0 ? Number(n.toFixed(2)) : 0
    },
    // 余额不足最低提现时禁用提交
    disableConfirm() {
      return this.balance < this.minAmount
    }
  },
  watch: {
    value(val) { 
		this.innerVisible = val 
		
	},
    innerVisible(val) { 
		console.log(val)
		this.$emit('input', val) 
	},
    isPhone(val) { this.layout.is_phone = val }
  },
  methods: {
    onAmountChange(v) {
      let x = Number(v)
      if (!Number.isFinite(x)) x = this.minAmount
      if (x < this.minAmount) x = this.minAmount
      if (x > this.balance) x = this.balance
      this.form.amount = Number(x.toFixed(2))
    },
    fillAll() {
      this.form.amount = Number(this.balance.toFixed(2))
    },
    pasteFromClipboard() {
      if (navigator.clipboard && navigator.clipboard.readText) {
        navigator.clipboard.readText()
          .then(t => {
            this.form.address = (t || '').trim()
            this.$message.success('已粘贴地址')
          })
          .catch(() => this.$message.warning('无法读取剪贴板，请手动粘贴'))
      } else {
        this.$message.info('浏览器限制，建议手动粘贴')
      }
    },
    quickCheckAddress() {
      if (!this.form.address) return this.$message.warning('请先输入地址')
      const ok = this.checkAddressByChain(this.form.address, this.form.chain)
      ok ? this.$message.success('地址校验通过') : this.$message.error('地址似乎不符合该网络规则，请再确认')
    },
    // 基础前端校验（后端仍需二次校验）
    checkAddressByChain(addr, chain) {
      const a = (addr || '').trim()
      if (!a) return false
      if (chain === 'TRC20') {
        // 简化：T 开头，Base58 字符 + 长度 34
        return /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(a)
      }
      if (chain === 'ERC20') {
        // 0x 开头 + 40位十六进制
        return /^0x[a-fA-F0-9]{40}$/.test(a)
      }
      return true
    },
    onCancel() {
      this.innerVisible = false
    },
    submit() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
    
        this.layout.loading = true;
    
        const payload = {
          amount: Number(this.form.amount),
          fee_rate: this.feeRate,           // 手续费比例（百分比的小数，如 0.012 = 1.2%）
          fee: this.feeAmount,              // = amount * fee_rate（已在前端计算保留两位）
          net_amount: this.netAmount,       // 实际到账
          chain: this.form.chain,
          address: this.form.address.trim(),
          remark: (this.form.remark || '').trim()
        };
    
        this.axios.post('/admin/user/subAgentGet', payload, { token: true })
          .then(res => {
            let all = res.data
            let list = all.data.list
    
            if (all.code) {
              this.$message.success(all.msg || '提交成功，等待审核');
              this.innerVisible = false;
              this.$emit('submitted', payload);   // 可选：通知父组件刷新
            } else {
              this.$message.error(all.msg || '提交失败');
            }
    
            this.layout.loading = false;
          })
          .catch(err => {
            //this.$message.error((err && err.message) || '提交失败，请稍后重试');
            this.layout.loading = false;
          });
      });
    }

  }
}
</script>

<style scoped>
.mb12 { margin-bottom: 12px; }
.mt12 { margin-top: 12px; }
.ml8  { margin-left: 8px; }
.w200 { width: 200px; }
.w100p { width: 100%; }

.summary-card {
  border-radius: 12px;
  padding: 10px;
}
.summary-card .sum-row {
  display: flex;
  justify-content: space-between;
  line-height: 32px;
  font-size: 14px;
}
.summary-card .sum-row b {
  font-weight: 600;
}
.summary-card .sum-row.total {
  font-size: 18px;
}
.summary-card .sum-foot {
  margin-top: 6px;
  color: #909399;
  font-size: 12px;
  text-align: right;
}
.addr-hint {
  color: #909399;
  font-size: 12px;
  margin-top: 4px;
}
</style>
